import { Component, type ReactNode } from "react";
import { notifyError } from "common/utils/app-monitoring/notify";
import { track } from "common/utils/mixpanel/tracker";
import { Button } from "@design-lab/shared/ui/design-system/Button";

type FallbackProps = {
  error: Error;
  reset: () => void;
};

type Props = {
  boundary: string;
  children: ReactNode;
  onReset?: () => void;
  fallback?: (props: FallbackProps) => ReactNode;
};

type State = {
  hasError: boolean;
  error: Error | null;
};

function DefaultFallback({ reset }: { reset: () => void }) {
  return (
    <div
      role="alert"
      aria-live="assertive"
      className="flex h-full min-h-64 items-center justify-center p-6"
    >
      <div className="max-w-sm text-center">
        <h2 className="text-xl font-semibold text-gray-900">Something went wrong.</h2>
        <p className="mt-2 text-sm text-gray-600">Your design has been saved.</p>
        <Button
          data-testid="design-lab-error-boundary-retry"
          variant="primary"
          onPress={reset}
          className="mt-4"
        >
          Try again
        </Button>
      </div>
    </div>
  );
}

/**
 * Catches render and lifecycle errors from descendants so a Design Lab feature can recover.
 *
 * React error boundaries do not catch event handler errors, async errors, timer or promise
 * errors that do not propagate to render, errors thrown by this boundary's own lifecycle or
 * render path, or import-time crashes during bundle evaluation.
 *
 * Mixpanel `boundary_rescued` semantics:
 * - `recovered: false` fires once on catch (deduped against StrictMode double-invoke).
 * - `recovered: true` fires from `componentDidUpdate` only when the boundary actually
 *   transitions out of its error state without immediately re-catching, so a click that
 *   re-throws does not look like a recovery in the funnel.
 */
export class DesignLabErrorBoundary extends Component<Props, State> {
  // Tracks the error reported during the current error cycle. Reset to `null` once the
  // boundary has emitted `recovered: true` for that cycle so a future re-catch reports cleanly.
  private reportedError: Error | null = null;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // React 18+ in <StrictMode> invokes componentDidCatch twice in dev. Skip the second
    // invocation for the same error instance so analytics and Honeybadger don't double-fire.
    if (this.reportedError === error) return;
    this.reportedError = error;

    notifyError(error, {
      context: { componentStack: info.componentStack, boundary: this.props.boundary }
    });

    this.trackRescue(error, false);
  }

  override componentDidUpdate(_prevProps: Props, prevState: State): void {
    // Recovery confirmation: only fire `recovered: true` once the boundary has actually
    // cleared without bouncing back to an error state. If the children re-throw on the
    // next render, `getDerivedStateFromError` flips `hasError` back to true synchronously
    // before this runs — so we never observe the cleared state, and `recovered: true`
    // does not fire for the failed retry.
    if (prevState.hasError && !this.state.hasError && this.reportedError) {
      this.trackRescue(this.reportedError, true);
      this.reportedError = null;
    }
  }

  private trackRescue = (error: Error, recovered: boolean): void => {
    try {
      track("boundary_rescued", {
        boundary: this.props.boundary,
        error_class: error.name,
        error_message: error.message,
        recovered
      });
    } catch {
      // Tracking must never turn a rescued render error into an uncaught crash.
    }
  };

  private handleReset = (): void => {
    this.setState({ hasError: false, error: null });

    try {
      this.props.onReset?.();
    } catch {
      // Reset handlers are best-effort recovery hooks.
    }
  };

  override render() {
    const { error, hasError } = this.state;

    if (hasError && error) {
      return (
        this.props.fallback?.({ error, reset: this.handleReset }) ?? (
          <DefaultFallback reset={this.handleReset} />
        )
      );
    }

    return this.props.children;
  }
}
